import { computeChallenge, randomSecret } from './pkce';

// sessionStorage is unreachable to an injected code, so none of the three match a callback it started.
const STATE_KEY = 'onward.sso-state';
const NONCE_KEY = 'onward.sso-nonce';
const VERIFIER_KEY = 'onward.sso-code-verifier';

const issueSecret = (key) => {
    const value = randomSecret();
    window.sessionStorage.setItem(key, value);

    return value;
};

export const issueSsoSecrets = async () => {
    const state = issueSecret(STATE_KEY);
    const nonce = issueSecret(NONCE_KEY);

    return { state, nonce, codeChallenge: await computeChallenge(issueSecret(VERIFIER_KEY)) };
};

// Compared before anything is removed: a replayed callback would otherwise burn the secrets of
// the sign-in actually in flight.
export const claimSsoSecrets = (state) => {
    if (!state || state !== window.sessionStorage.getItem(STATE_KEY)) {
        throw new Error('SSO sign-in expired, please start again');
    }

    const nonce = window.sessionStorage.getItem(NONCE_KEY);
    const code_verifier = window.sessionStorage.getItem(VERIFIER_KEY);

    [STATE_KEY, NONCE_KEY, VERIFIER_KEY].forEach((key) => window.sessionStorage.removeItem(key));

    if (!nonce || !code_verifier) {
        throw new Error('SSO sign-in expired, please start again');
    }

    return { nonce, code_verifier };
};

// The endpoint comes from the backend's discovery call: no vendor's authorize path can be
// derived from its issuer.
export const authorizeUrl = (sso, { state, nonce, codeChallenge }) => {
    if (!sso?.authorization_endpoint || !sso?.client_id || !sso?.type || !sso?.redirect_uri) {
        throw new Error('SSO is not configured');
    }

    const url = new URL(sso.authorization_endpoint);

    Object.entries({
        client_id: sso.client_id,
        // The backend's, never rebuilt here: it redeems the code against the same string, and
        // RFC 6749 4.1.3 wants the two byte-identical. Building it twice is how they drift.
        redirect_uri: sso.redirect_uri,
        response_type: 'code',
        response_mode: 'query',
        scope: 'openid email profile',
        state,
        nonce,
        code_challenge: codeChallenge,
        code_challenge_method: 'S256',
    }).forEach(([key, value]) => url.searchParams.set(key, value));

    return url.toString();
};

// Keyed on the codes RFC 6749 4.1.2.1 and OIDC Core 3.1.2.6 define, so a code we have never seen
// falls to the generic line rather than to the provider's prose.
const REFUSAL_COPY = {
    access_denied:
        "Your organization's identity provider refused this sign-in. Your account is most likely not assigned to Onward yet — ask your IT administrator to grant you access, then try again.",
    consent_required:
        "Your organization has not approved Onward's access to your directory. Ask your IT administrator to approve it, then try again.",
    login_required: 'Your identity provider session ended before sign-in finished. Please start again.',
    interaction_required: 'Your identity provider needs you to sign in again. Please start again.',
    server_error: 'Your identity provider reported an error. Please try again in a few minutes.',
    temporarily_unavailable: 'Your identity provider is temporarily unavailable. Please try again in a few minutes.',
};

const GENERIC_REFUSAL =
    "Your organization's identity provider refused this sign-in. Ask your IT administrator whether your account has access to Onward.";

export const readAuthorizationResponse = (search) => {
    const params = new URLSearchParams(search);

    // error_description is attacker-supplied prose on our own origin, so it is logged for support
    // and never rendered: on the page it would read as ours, above a real sign-in link.
    const error = params.get('error');
    if (error) {
        const refusal = error.replace(/[^\w-]/g, '').slice(0, 64);

        const description = params.get('error_description');
        if (description) {
            console.error(`SSO provider refused the sign-in: ${refusal} — ${description}`);
        }

        // Not a bare lookup: to a crafted link, constructor and toString are codes too.
        const hasCopy = Object.prototype.hasOwnProperty.call(REFUSAL_COPY, refusal);
        const refusalCopy = hasCopy ? REFUSAL_COPY[refusal] : GENERIC_REFUSAL;

        throw new Error(refusal ? `${refusalCopy} (${refusal})` : refusalCopy);
    }

    const code = params.get('code');
    const state = params.get('state');

    if (!code || !state) {
        throw new Error('SSO sign-in expired, please start again');
    }

    return { code, state };
};
